import React, { ReactElement, useCallback, useState, useSyncExternalStore } from "react";
import { VisState, prepareVissStatesByIds } from "../../helpers/vis";
import {
  deductCurrentPumpStateStatuses,
  useContentProps as usePumpVisContentProps,
} from "../pumping/Vis";
import VisContentWithChrome, {
  VisRawContent as VisPumpContent,
} from "../pumping/components/VisContent";
import { VissGroup } from "../../components/VissGroup";
import { Settings } from ".";
import { Settings as NestedSettings } from "../pumping/index";
import MapWithMarkers, { MarkerProps } from "../../components/MapWithMarkers";
import ReactVisBase from "../../components/ReactVisBase";
import { ObjectState } from "../../components/object-state";
import oc from "open-color";
import IndicatorBlock from "./IndicatorBlock";
import { notNull } from "@overvis/ts-shared";
import useRealtimeParamsData from "../../hooks/use-realtime-params-data";
import { IoIosSearch } from "react-icons/io";
import { isMarkerValueMatchCondition } from "../../helpers/utils";

export default function Vis({ state }: { state: VisState<Settings> }): React.ReactElement {
  const nestedStates = useNestedStates(state);

  return (
    <ReactVisBase className="vis-pumping-dashboard" state={state}>
      {nestedStates.length > 0 && <Content state={state} nestedStates={nestedStates} />}
    </ReactVisBase>
  );
}

interface AdditionalSettings extends Record<string, unknown> {
  showInteractiveElements: boolean;
  useShortFormatForCounters: boolean;
  shortStatesColors?: NestedSettings["shortStatesColors"];
  shortStates?: NestedSettings["shortStates"];
}

function useNestedStates(state: VisState<Settings>): VisState<NestedSettings>[] {
  const [pumpingsStates, setPumpingsStates] = React.useState<VisState<NestedSettings>[]>([]);

  React.useEffect(() => {
    const vissIds = state.settings.groups.map((i) => i.visIds.map(Number)).flat();
    if (!vissIds?.length) {
      return;
    }

    (async () => {
      const additionalSettings: AdditionalSettings = {
        showInteractiveElements: state.settings.showInteractiveElements,
        useShortFormatForCounters: true,
      };
      if (state.settings.shortStatesColors) {
        additionalSettings.shortStatesColors = state.settings.shortStatesColors;
      }
      if (state.settings.shortStates) {
        additionalSettings.shortStates = state.settings.shortStates;
      }
      const states = await prepareVissStatesByIds<NestedSettings>(
        state.org.orgId,
        vissIds,
        {
          nets: state.org.nets,
          devices: state.org.devices,
          params: state.org.params,
        },
        additionalSettings,
        state.params.watcher,
      );
      setPumpingsStates([...states]);
    })().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });
  }, []);

  return pumpingsStates;
}

function calculateObjectState(
  data: Readonly<Record<string, string>>,
  pumpStateOnValue: number,
  compareOperation: NestedSettings["pumpState"]["compareOperation"],
  alarmsSettings: NestedSettings["generalAlarm"],
): ObjectState {
  const pumpState = data.pumpStateDashboard;
  if (pumpState === "" || pumpState === undefined || pumpState === "NORT") {
    return "no-connection";
  }

  if (
    data.generalAlarmDashboard &&
    alarmsSettings &&
    isMarkerValueMatchCondition(
      alarmsSettings.alarmMarkerValue,
      alarmsSettings.compareOperation,
      data.generalAlarmDashboard,
    )
  ) {
    return "error";
  }

  if (isMarkerValueMatchCondition(pumpStateOnValue, compareOperation, pumpState)) {
    return "power-on";
  }

  return "power-off";
}

type WatchedParams =
  | "pumpStateDashboard"
  | "generalAlarmDashboard"
  | `workMode${number}MarkerDashboard`;

function useIndicationBlockAttrs(
  state: VisState<NestedSettings>,
  vissOpenStates: Record<string, boolean>,
  setVissOpenStates: (value: Record<string, boolean>) => void,
  showTooltips: boolean,
  addGoToNetLinks: boolean,
): {
  onClick: () => void;
  isOpen: boolean;
  backgroundColor: string;
  label: string;
  tooltipName?: string;
  goToNetLink?: string;
  isMaintenanceDate: boolean;
} {
  const visId = state.visId;
  const isOpen = vissOpenStates[visId] ?? false;

  let label = notNull(state.settings.shortName);
  if (!label) {
    const defaultNet = state.org.getDefaultNet();
    label = defaultNet?.name || state.settings.shortName || `#${visId}`;
  }

  const watchedParams: Partial<Record<WatchedParams, string>> = {
    pumpStateDashboard: state.settings.pumpState.paramPath,
    generalAlarmDashboard: state.settings.generalAlarm?.paramPath,
  };
  if (state.settings.workModes) {
    for (let i = 0; i < state.settings.workModes.length; i++) {
      watchedParams[`workMode${i}MarkerDashboard`] = state.settings.workModes[i].marker.paramPath;
    }
  }

  const params: [storeKey: WatchedParams, path: string, update: number][] = [];
  for (const [key, path] of Object.entries(watchedParams)) {
    if (path) {
      params.push([key as WatchedParams, path, state.settings.updatePeriodSec]);
    }
  }
  useRealtimeParamsData(state.params, params);
  const data = useSyncExternalStore(state.data.subscribe, state.data.getSnapshot);

  let backgroundColor = oc.gray[5];
  let currentWorkModeName: string | undefined;
  for (let i = 0; i < state.settings.workModes.length; i++) {
    if (
      data[`workMode${i}MarkerDashboard`] === state.settings.workModes[i].marker.value.toString()
    ) {
      currentWorkModeName = state.settings.workModes[i].name;
      break;
    }
  }
  const { shortStateName } = deductCurrentPumpStateStatuses(
    state.settings,
    data.pumpStateDashboard,
    data.generalAlarmDashboard,
    currentWorkModeName,
  );
  if (shortStateName) {
    backgroundColor = state.settings.shortStatesColors[shortStateName];
  }

  const onClick = useCallback(() => {
    setVissOpenStates({
      ...vissOpenStates,
      [visId]: !isOpen,
    });
  }, [vissOpenStates, visId, isOpen]);

  const defaultNet = state.org.getDefaultNet();

  let tooltipName;
  if (showTooltips && defaultNet) {
    tooltipName = defaultNet.name;
  }

  let goToNetLink;
  if (addGoToNetLinks && defaultNet) {
    goToNetLink = `/manage/network/${defaultNet.slug}/`;
  }

  const maintenanceSavedDateStr = state.visData.get("maintenceDate");
  let isMaintenanceDate = false;
  if (maintenanceSavedDateStr) {
    isMaintenanceDate =
      state.settings.showMaintenanceDate && new Date() >= new Date(maintenanceSavedDateStr);
  }

  return {
    onClick,
    isOpen,
    backgroundColor,
    label,
    tooltipName,
    goToNetLink,
    isMaintenanceDate,
  };
}

function VisPumpContentWithProps({ state }: { state: VisState<NestedSettings> }): ReactElement {
  return <VisPumpContent {...usePumpVisContentProps(state)} />;
}

function Content({
  state,
  nestedStates,
}: {
  state: VisState<Settings>;
  nestedStates: VisState<NestedSettings>[];
}): ReactElement {
  const [vissOpenStates, setVissOpenStates] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState("");

  const vissIdNamesMap = new Map(
    nestedStates.map((visState) => {
      return [
        visState.visId,
        `${visState.settings.shortName} ${visState.org.getDefaultNet()?.name}`,
      ];
    }),
  );

  const markersData: MarkerProps[] = nestedStates.map((visState) => {
    return {
      coordinates: visState.settings.mapMarkerCoordinates ?? undefined,
      subscribeToVisData: visState.data.subscribe,
      getVisDataSnapshot: visState.data.getSnapshot,
      name: visState.settings.shortName || "Well pump",
      stateCalculator: useCallback(
        (data) =>
          calculateObjectState(
            data,
            visState.settings.pumpState.onValue,
            visState.settings.pumpState.compareOperation,
            visState.settings.generalAlarm,
          ),
        [visState],
      ),
      onClick: useCallback(() => {
        const defaultNet = visState.org.getDefaultNet();
        if (!defaultNet || !state.settings.addGoToNetLinks) {
          return;
        }
        window.open(`/manage/network/${defaultNet.slug}/`);
      }, [visState]),
    };
  });

  const viss = nestedStates.map((visState) => {
    return state.settings.enableIndicationView ? (
      <IndicatorBlock
        key={visState.visId}
        {...useIndicationBlockAttrs(
          visState,
          vissOpenStates,
          setVissOpenStates,
          state.settings.showTooltips,
          state.settings.addGoToNetLinks,
        )}
      >
        <VisPumpContentWithProps state={visState} />
      </IndicatorBlock>
    ) : (
      <VisContentWithChrome
        key={visState.visId}
        {...usePumpVisContentProps(visState)}
      ></VisContentWithChrome>
    );
  });

  return (
    <div>
      {state.settings.showMap && (
        <div style={{ marginBottom: "30px" }}>
          <MapWithMarkers
            mapApiKey={state.settings.showMap.apiKey}
            mapCoordinates={
              state.settings.showMap.defaultViewCoordinates as [lat: number, lng: number]
            }
            markersData={markersData}
            defaultZoom={state.settings.showMap.defaultZoom}
            colors={{
              "no-connection": oc.gray[6],
              "power-off": oc.gray[6],
              "power-on": oc.green[6],
              error: oc.red[6],
            }}
          />
        </div>
      )}
      {state.settings.showSearch && (
        <div
          style={{
            marginBottom: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
          }}
        >
          <div style={{ marginRight: "-29px", paddingLeft: "5px", zIndex: 100 }}>
            <IoIosSearch size={22} color="#aca9a9" />
          </div>
          <input
            style={{
              width: "350px",
              padding: "15px 10px",
              borderRadius: "2px",
              boxShadow: "inset 0 1px 1px #00000013",
              color: "#495057",
              border: "1px solid #ced4da",
              paddingLeft: "30px",
            }}
            type="text"
            value={searchText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchText(e.target.value);
            }}
          />
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${state.settings.groups.length}, 1fr)`,
          gap: "5px",
        }}
      >
        {state.settings.groups.map((group, i) => {
          const filteredViss = viss
            .filter((v) => {
              if (searchText.length >= 3) {
                return (
                  group.visIds.includes(v.key!) &&
                  vissIdNamesMap.get(v.key!)?.toLowerCase().includes(searchText.toLowerCase())
                );
              }
              return group.visIds.includes(v.key!);
            })
            .sort((a, b) => {
              if (!a.key || !b.key) {
                return 1;
              }
              return group.visIds.indexOf(a.key) - group.visIds.indexOf(b.key);
            });
          const isLastGroup = i >= state.settings.groups.length - 1;
          return (
            <div
              key={i}
              style={{
                borderRight: isLastGroup ? "none" : "1px solid #d5d5d5",
                paddingRight: isLastGroup ? "none" : "5px",
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                alignItems: "center",
              }}
            >
              {group.name && <div style={{ fontWeight: "bold" }}>{group.name}</div>}
              <VissGroup
                viss={filteredViss}
                minItemWidth={state.settings.minItemWidth}
                maxItemWidth={state.settings.maxItemWidth}
              />
            </div>
          );
        })}
      </div>
    </div>
  );

  // React.useEffect(() => {
  //   let vissIds = state.settings.visIds;
  //   if (state.settings.groupedVisIds) {
  //     vissIds = state.settings.groupedVisIds.map((i) => i.visIds).flat();
  //   }
  //   if (!vissIds?.length) {
  //     return;
  //   }

  //   (async () => {
  //     const states = await prepareVissStatesByIds<NestedSettings>(
  //       state.org.orgId,
  //       vissIds.map(Number),
  //       {
  //         nets: state.org.nets,
  //         devices: state.org.devices,
  //         params: state.org.params,
  //       },
  //       { isDashboard },
  //       state.params.watcher,
  //     );
  //     setPumpingsStates([...states]);
  //   })().catch((e) => {
  //     // eslint-disable-next-line no-console
  //     console.error(e);
  //   });
  // }, []);

  // // const changeViewHandler = (visId: string, show: boolean): void => {
  // //   // const pumpState = pumpingsStates.find((s) => s.visId === visId);
  // //   // pumpState?.data.set("isSmallView", show ? "true" : "");
  // // };

  // const objectStateCalculator = (data: Readonly<Record<string, string>>): ObjectState => {
  //   const pumpState = data.pumpState;
  //   if (pumpState === undefined) {
  //     return "no-connection";
  //   }
  //   if (pumpState) {
  //     return "power-on";
  //   } else {
  //     return "power-off";
  //   }
  // };

  // const viss = pumpingsStates.map((visState) => {
  //   const props = usePumpVisContentProps(visState);
  //   // visState.data.set("isSmallView", state.settings.withSmallView ? "true" : "");
  //   return (
  //     <div>
  //       <IndicationBlock />
  //       <VisPumpContent {...props} />
  //     </div>
  //   );
  // });
  // const markersData: MarkerProps[] = pumpingsStates.map((visState) => {
  //   return {
  //     coordinates: visState.settings.mapMarkerCoordinates ?? undefined,
  //     subscribeToVisData: visState.data.subscribe,
  //     getVisDataSnapshot: visState.data.getSnapshot,
  //     name: visState.settings.shortName || "Well pump",
  //   };
  // });

  // return (
  //   <>
  //     {state.settings.addMap && state.settings.mapCoordinates && (
  //       <div style={{ marginBottom: "20px" }}>
  //         <MapWithMarkers
  //           mapApiKey="AIzaSyBS_Hhaufu86mGLyboaytGtH0QbKuaSEQU"
  //           mapCoordinates={state.settings.mapCoordinates}
  //           markersData={markersData}
  //           objectStateCalculator={objectStateCalculator}
  //           colors={{
  //             "no-connection": oc.gray[6],
  //             "power-off": oc.gray[6],
  //             "power-on": oc.green[6],
  //             error: oc.red[6],
  //           }}
  //         />
  //       </div>
  //     )}
  //     {state.settings.groupedVisIds ? (
  //       <div
  //         style={{
  //           display: "grid",
  //           gridTemplateColumns: `repeat(${state.settings.groupedVisIds.length}, 1fr)`,
  //           gap: "5px",
  //         }}
  //       >
  //         {state.settings.groupedVisIds.map((group, i) => {
  //           const filteredViss = viss.filter((v) => group.visIds.includes(v.key!));
  //           const isLast = i >= state.settings.groupedVisIds!.length - 1;
  //           return (
  //             <div
  //               key={i}
  //               style={{
  //                 borderRight: isLast ? "none" : "1px solid #d5d5d5",
  //                 paddingRight: isLast ? "none" : "5px",
  //                 display: "flex",
  //                 flexDirection: "column",
  //                 gap: "15px",
  //                 alignItems: "center",
  //               }}
  //             >
  //               <div style={{ fontWeight: "bold" }}>{group.name}</div>
  //               <VissGroup viss={filteredViss} oneRowItems={3} />
  //             </div>
  //           );
  //         })}
  //       </div>
  //     ) : (
  //       <VissGroup viss={viss} oneRowItems={state.settings.oneRowItems} />
  //     )}
  //   </>
  // );
}
